import React, { Fragment, useEffect, useState } from 'react';
import {
  Button,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@mui/material';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import {
  createSegment,
  getLatestSegmentCode
} from 'src/services/projectService';
import { AppActions } from 'src/store/AppReducer';
import { RootStateType } from 'src/store/RootReducer';

export interface SegmentInformation {
  code: string;
  description: string;
  name: string;
  manager: string;
}

const AddForm = ({ toggle, open, getData, length,setSnackBarMessageS , setSnackbarSeverityS, setSnackBarOpenS, getSegmentData }) => {
  const dispatch = useDispatch();
  const TextFieldStyle = {
    paddingTop: 2,
    paddingLeft: 15,
    paddingRight: 15,
    fontSize: '13px'
  };

  const LabelStyle = {
    paddingLeft: 15,
    paddingRight: 15,
    fontSize: '13px'
  };

  const { register, handleSubmit, reset } = useForm<SegmentInformation>();
  const [nameError, setNameError] = useState('');
  const [descError, setDescError] = useState('');
  const [latestCode, setLatestCode] = useState('');

  const useAppSelector: TypedUseSelectorHook<RootStateType> = useSelector;
  const { isLoading } = useAppSelector((state) => state.appReducer);

  const onSubmit = (data: SegmentInformation) => {
    if (
      data.name !== '' &&
      data.name.trim().length > 0 &&
      data.name.length < 31 &&
      data.description.length < 101
    ) {
      dispatch(AppActions.showLoader());

      setNameError('');
      const body = {
        code: latestCode,
        name: data.name,
        description: data.description
      };

      createSegment(body)
        .then(() => {
          reset();
          getData();
          getSegmentData();
          setSnackBarMessageS('You have created a segment successfully.');
          setSnackbarSeverityS('success');
          setSnackBarOpenS(true);        
          toggle();
          dispatch(AppActions.hideLoader());
        })
        .catch((error) => {
          dispatch(AppActions.hideLoader());
          setSnackBarMessageS(error?.request?.response ? error?.request?.response : 'Unable to add segment.');
          setSnackbarSeverityS('error');
          setSnackBarOpenS(true);
          toggle();
        });
    } else if (data.name === '') {
      setNameError('Segment name required');
    } else if (data.name.trim().length < 1) {
      setNameError('Segment name cannot be only spaces');
    }
  };

  const getLatestAccountValue = async () => {
    const result: any = await getLatestSegmentCode();
    const value = result.data.replace(/\D/g, '');

    const padLeft = (nr?: any, n?: any, str?: any) => {
      return Array(n - String(nr).length + 1).join(str || '0') + nr;
    };

    if (value === '001' && length < 1) {
      setLatestCode(`S${value}`);
    } else {
      setLatestCode(`S${padLeft(Number(value) + 1, 3)}`);
    }
  };

  useEffect(() => {
    if (open) getLatestAccountValue();
  }, [open, length]);

  const onCancel = () => {
    reset();
    toggle();
    setNameError('');
  };

  return (
    <Fragment>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid item xs={12} sx={{ pt: 3, pb: 1, pl: 2 }}>
          <Typography style={{ fontSize: '15px' }} variant="h3">
            Create New Segment
          </Typography>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography style={LabelStyle} fontWeight="light" variant="h5">
              Segment Name<span style={{ color: 'red' }}>*</span>
            </Typography>
            <TextField
              id="segmentName"
              name="segmentName"
              inputProps={{ maxLength: 30 }}
              size="small"
              {...register('name', {})}
              style={TextFieldStyle}
              fullWidth
              placeholder="Enter Segment Name"
              variant="outlined"
              onChange={(e) => {
                if (e.target.value.length > 30) {
                  setNameError('Segment name must not exceed 30 characters');
                } else {
                  setNameError('');
                }
              }}
            />
            <Typography
              style={{
                paddingLeft: 15,
                paddingRight: 15,
                fontSize: '11px'
              }}
              color="red"
            >
              {nameError}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography style={LabelStyle} fontWeight="light" variant="h5">
              Segment Code
            </Typography>
            <TextField
              id="segmentCode"
              name="segmentCode"
              className="disabledTextbox"
              aria-readonly
              size="small"
              sx={{
                input: {
                  background: '#d1d1d1',
                  borderRadius: 0.5
                }
              }}
              {...register('code', {})}
              style={{
                ...TextFieldStyle,
                pointerEvents: 'none'
              }}
              fullWidth
              placeholder="Segment Code"
              variant="outlined"
              value={latestCode}
            />
          </Grid>

          <Grid item xs={6}>
            <Typography style={LabelStyle} fontWeight="light" variant="h5">
              Description
            </Typography>
            <TextField
              id="segmentDescription"
              name="segmentDescription"
              inputProps={{ maxLength: 100 }}
              size="small"
              {...register('description', {})}
              style={TextFieldStyle}
              fullWidth
              placeholder="Enter Description"
              variant="outlined"
              onChange={(e) => {
                if (e.target.value.length < 101) {
                  setDescError('');
                } else {
                  setDescError(
                    'Account description must not exceed 100 characters'
                  );
                }
              }}
              rows={2}
              multiline
            />
            <Typography
              style={{
                paddingLeft: 15,
                paddingRight: 15,
                fontSize: '11px'
              }}
              color="red"
            >
              {descError}
            </Typography>
          </Grid>
          <Grid
            item
            sx={{
              mb: 2,
              mt: 2,
              pr: 2,
              display: 'flex',
              justifyContent: 'flex-end'
            }}
            xs={12}
          >
            <Button
              id="segment-btn-onCancel"
              sx={{
                width: '10%',
                fontWeight: 'normal',
                background: '#F5F7FF',
                fontSize: '13px'
              }}
              onClick={() => onCancel()}
            >
              Cancel
            </Button>
            <Button
              id="segment-btn-onSubmit"
              disabled={isLoading}
              className={isLoading ? 'loading' : ''}
              sx={{
                ml: 2,
                width: '10%',
                fontSize: '13px',
                fontWeight: 'normal',
                background: '#0069E4'
              }}
              variant="contained"
              type="submit"
            >
              Add
            </Button>
          </Grid>
        </Grid>
      </form>
    </Fragment>
  );
};

export default AddForm;
